import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EMPTY_VIEW_ICONS, EmptyView } from '@shared/components/ui/EmptyView';
import { useToast } from '@shared/hooks/useToast';
import { useReadyToPrepareByHourReport } from '../../hooks/useReadyToPrepareByHourReport';
import type { ReadyToPrepareByHourQuery } from '../../models';
import { FiltersBar, type ReadyToPrepareFiltersValue } from '../interaction/FiltersBar';
import { ClientHourRangesChart } from '../display/ClientHourRangesChart';
import { OrdersByHourChart } from '../display/OrdersByHourChart';
import { LoadingState } from '../feedback/LoadingState';

const formatDateLocal = (date: Date): string => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

export const Main = () => {
  const { t } = useTranslation('readyToPrepareByHourModule');
  const { toast } = useToast();

  const getDefaultRange = useCallback((): ReadyToPrepareFiltersValue => {
    const end = new Date();
    const start = new Date(end.getFullYear(), end.getMonth(), 1);
    return {
      dateFrom: formatDateLocal(start),
      dateTo: formatDateLocal(end),
    };
  }, []);

  const [range, setRange] = useState<ReadyToPrepareFiltersValue>(() => getDefaultRange());

  const queryParams: ReadyToPrepareByHourQuery | null = useMemo(
    () => ({
      date_from: range.dateFrom,
      date_to: range.dateTo,
    }),
    [range.dateFrom, range.dateTo],
  );

  const { data, isLoading, isFetching, error, isError } = useReadyToPrepareByHourReport(queryParams);

  useEffect(() => {
    if (!isError || !error) return;
    const message = error instanceof Error ? error.message : t('error.load_failed', 'No se pudo cargar el informe.');
    toast({
      title: t('error.title', 'Error'),
      description: message,
      variant: 'destructive',
    });
  }, [isError, error, toast, t]);

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  useEffect(() => {
    if (isInitialLoad && !isLoading) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  const handleApply = useCallback((next: ReadyToPrepareFiltersValue) => {
    setRange(next);
  }, []);

  const hasErrorNoData = isError && !data;
  const isEmptyZero = data && data.meta.total_orders === 0 && (!data.rows || data.rows.length === 0);

  if (isLoading && isInitialLoad) {
    return <LoadingState />;
  }

  return (
    <div className="px-6 pt-4 pb-10 space-y-6">
      <FiltersBar value={range} onApply={handleApply} getDefaultRange={getDefaultRange} />

      {hasErrorNoData ? (
        <EmptyView
          type="emptyState"
          icon={EMPTY_VIEW_ICONS.ORDERS}
          title={t('empty.error.title', 'Error al cargar datos')}
          description={t(
            'empty.error.description',
            'No se pudieron cargar los datos del informe. Por favor, intenta de nuevo.',
          )}
        />
      ) : (
        /* Contenedor con opacidad durante fetching (mismo patrón que Logística) */
        <div
          className={`space-y-6 transition-opacity duration-300 ${
            isFetching ? 'opacity-50 pointer-events-none' : 'opacity-100'
          }`}
          aria-busy={isFetching}
        >
          {isEmptyZero ? (
            <EmptyView
              type="emptyState"
              icon={EMPTY_VIEW_ICONS.ORDERS}
              title={t('empty.no_data.title', 'Sin datos en el periodo')}
              description={t(
                'empty.no_data.description',
                'No hay pedidos con fecha de listo para preparar en el rango seleccionado.',
              )}
            />
          ) : (
            data && (
              <>
                <OrdersByHourChart rows={data.rows} />
                <ClientHourRangesChart rows={data.rows} />
              </>
            )
          )}
        </div>
      )}
    </div>
  );
};
